import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NativeFileInput } from '@/shared/ui';
import { Text } from '@/shared/ui';
import { Button } from '@/shared/ui/components/shadcn/button';
import { useIsMobile } from '@/shared/hooks';
import { VistoryService } from '../services/vistory-service';

interface Props {
  agencyId: string;
  vistoryId: string;
  roomId?: string;
  onUploaded: () => void;
}

/**
 * Uploads one photo to storage and records its metadata.
 *
 * Throws on failure rather than swallowing it. The empty `catch` this
 * replaces is what let four independent contract mismatches survive
 * here: the broker photographed every room, watched the bar reach the
 * end, and stored nothing.
 *
 * @param agencyId - The owning agency.
 * @param vistoryId - The vistory the photo belongs to.
 * @param file - The chosen image.
 * @param roomId - The room, when uploading into one.
 */
async function uploadSingleFile(
  agencyId: string, vistoryId: string, file: File, roomId?: string,
): Promise<void> {
  const { url, fields } = await VistoryService.getPhotoUploadUrl(
    agencyId, vistoryId, { filename: file.name, content_type: file.type },
  );
  const formData = new FormData();
  Object.entries(fields).forEach(([k, v]) => formData.append(k, v));
  formData.append('file', file);
  const stored = await fetch(url, { method: 'POST', body: formData });
  if (stored && stored.ok === false) {
    throw new Error(`upload rejected for ${file.name}`);
  }
  await VistoryService.addPhotoMetadata(agencyId, vistoryId, {
    photo_key: fields.key,
    filename: file.name,
    content_type: file.type,
    room_id: roomId,
  });
}

/**
 * Photo uploader for a vistory (or a specific room), with gallery and camera inputs.
 */
export function VistoryPhotoUploader({ agencyId, vistoryId, roomId, onUploaded }: Props) {
  const { t } = useTranslation('features/vistories');
  const galleryRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [total, setTotal] = useState(0);
  const [failed, setFailed] = useState<string[]>([]);
  const isMobile = useIsMobile();

  function resetFileInputs() {
    if (galleryRef.current) galleryRef.current.value = '';
    if (cameraRef.current) cameraRef.current.value = '';
  }

  /** Uploads each file, counting only the ones that actually stored. */
  async function uploadAllFiles(files: FileList): Promise<string[]> {
    const failures: string[] = [];
    let stored = 0;
    for (let i = 0; i < files.length; i++) {
      try {
        await uploadSingleFile(agencyId, vistoryId, files[i], roomId);
        stored += 1;
        setProgress(stored);
      } catch {
        failures.push(files[i].name);
        setFailed([...failures]);
      }
    }
    return failures;
  }

  async function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    setUploading(true);
    setTotal(files.length);
    setProgress(0);
    setFailed([]);
    await uploadAllFiles(files);
    setUploading(false);
    onUploaded();
    resetFileInputs();
  }

  const barWidth = total > 0 ? (progress / total) * 100 : 0;

  return (
    <div className="flex flex-col gap-[var(--space-2)]">
      <NativeFileInput
        ref={galleryRef}
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
      />
      {isMobile && (
        <NativeFileInput
          ref={cameraRef}
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
        />
      )}
      <div className="flex gap-[var(--space-2)]">
        {isMobile && (
          <Button variant="outline" size="sm" onClick={() => cameraRef.current?.click()} disabled={uploading}>
            {t('vistory.take_photo')}
          </Button>
        )}
        <Button variant="outline" size="sm" onClick={() => galleryRef.current?.click()} disabled={uploading}>
          {uploading ? `${t('vistory.uploading')} (${progress}/${total})` : t('vistory.choose_gallery')}
        </Button>
      </div>
      {uploading && (
        <div className="h-[var(--space-1-5)] w-full rounded-full bg-[var(--surface-tertiary)] overflow-hidden">
          <div className="h-full bg-[var(--seed-base)] transition-[width] duration-300" style={{ width: `${barWidth}%` }} />
        </div>
      )}
      {total > 0 && !uploading && (
        <Text
          data-testid="vistory-upload-summary"
          data-stored={progress}
          data-total={total}
          className="text-[var(--text-tertiary)]"
          style={{ fontSize: 'var(--text-xs)' }}
        >
          {t('vistory.upload_stored', { stored: progress, total })}
        </Text>
      )}
      {failed.length > 0 && (
        <div
          data-testid="vistory-upload-failures"
          className="flex flex-col gap-(--space-1) text-[var(--accent-cobre)]"
          style={{ fontSize: 'var(--text-xs)' }}
        >
          <Text component="span" className="[font-weight:var(--font-semibold)]">
            {t('vistory.upload_failed_title', { count: failed.length })}
          </Text>
          {failed.map((name) => (
            <Text component="span" key={name}>{name}</Text>
          ))}
        </div>
      )}
    </div>
  );
}
